import { AddMentorship } from "@/components/users/user-details/mentorships/add-mentorship";
import { UpdateMentorship } from "@/components/users/user-details/mentorships/update-mentorship";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import type { Mentorship } from "@/lib/api/mentorships";
import { useGetUserMentorships } from "@/lib/api/mentorships";
import {
  useGetUserLeadMentors,
  useGetUserSupervisorAndChannel,
  useUsers,
  type User,
} from "@/lib/api/user";
import { useAbility } from "@/lib/casl/ability-context";
import { useAssignSupervisorMentor } from "@/lib/hooks/mentors/use-assign-supervisor-mentors";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { isMentorIdentity } from "@/lib/utils/system-roles";
import { formatDaysInMentorship } from "./format-days-in-mentorship";
import {
  groupMentorshipRows,
  reactivateMentorshipDisabled,
  sortActiveMentorships,
  type ActiveMentorshipSort,
  type MentorshipSortColumn,
} from "./group-mentorship-rows";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton, UserAvatar } from "@somethings-ui/web";
import { Link } from "@tanstack/react-router";
import {
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  ChevronUp,
  Ellipsis,
  Loader2,
  MessageCircle,
} from "lucide-react";
import { useState, type ReactNode } from "react";

const ROW_GRID = "grid grid-cols-2 items-center";
interface MentorshipsTabProps {
  user: User;
  canReadMentorships: boolean;
}

export function MentorshipsTab({
  user,
  canReadMentorships,
}: MentorshipsTabProps) {
  const isMentor = isMentorIdentity(user.systemRoles);
  const { data: mentorships, isPending } = useGetUserMentorships(
    user.id,
    canReadMentorships,
  );
  const supervisorQuery = useGetUserSupervisorAndChannel(user.id, isMentor);
  const leadMentorQuery = useGetUserLeadMentors(user.id, isMentor);
  const teamLoading =
    isMentor && (supervisorQuery.isPending || leadMentorQuery.isPending);
  const supervisor =
    supervisorQuery.data?.is_supervisor === false
      ? undefined
      : supervisorQuery.data?.supervisor;
  const leadMentors = leadMentorQuery.data ?? [];
  const [pausedOpen, setPausedOpen] = useState(true);
  const [pastOpen, setPastOpen] = useState(true);
  const [activeSort, setActiveSort] = useState<ActiveMentorshipSort | null>(
    null,
  );
  const grouped = groupMentorshipRows(mentorships ?? []);
  const activeRows = sortActiveMentorships(
    grouped.active,
    activeSort,
    isMentor,
  );
  const toggleActiveSort = (column: MentorshipSortColumn) => {
    setActiveSort((current) => {
      if (current?.column !== column) return { column, direction: "asc" };
      return {
        column,
        direction: current.direction === "asc" ? "desc" : "asc",
      };
    });
  };
  const hasCurrentRows =
    grouped.active.length + grouped.paused.length > 0;
  const noActiveWithPast =
    !hasCurrentRows && grouped.past.length > 0;
  const pastLabel = isMentor ? "Past Mentees" : "Past Mentors";

  return (
    <div className="flex flex-col gap-6">
      {teamLoading ? (
        <TeamSkeleton />
      ) : isMentor ? (
        <section>
          <h2 className="font-heading text-base font-bold leading-6 text-text-primary">
            Team
          </h2>
          <div className="mt-2 flex flex-wrap gap-4">
            <TeamSlot
              emptyLabel="No supervisor assigned"
              people={
                supervisor
                  ? [
                      {
                        id: supervisor.id,
                        name: supervisor.name,
                        profilePictureUrl: supervisor.profile_picture_url,
                        roleLabel: "Supervisor",
                      },
                    ]
                  : []
              }
              action={
                <TeamAssignmentEditor
                  mentorId={user.id}
                  currentId={supervisor?.id ?? null}
                  role="supervisor"
                />
              }
            />
            <TeamSlot
              emptyLabel="No lead mentor"
              people={leadMentors.map((leadMentor) => ({
                id: leadMentor.id,
                name: leadMentor.name,
                profilePictureUrl: leadMentor.profile_picture_url,
                roleLabel: "Lead Mentor",
              }))}
              action={
                <TeamAssignmentEditor
                  mentorId={user.id}
                  currentId={leadMentors[0]?.id ?? null}
                  role="lead_mentor"
                />
              }
            />
          </div>
        </section>
      ) : null}

      <section>
        <div className="flex h-8 items-center justify-between">
          <h2 className="font-heading text-base font-bold leading-6 text-text-primary">
            {isMentor
              ? isPending
                ? "Mentees"
                : `Mentees (${grouped.active.length})`
              : "Mentors"}
          </h2>
          <AddMentorship
            userId={user.id}
            isMentor={isMentor}
            isMentee={!isMentor}
            triggerLabel={isMentor ? "Add mentee" : "Add mentor"}
            triggerClassName="h-8 gap-1 rounded-full border-[#dfddd8] bg-white px-3 font-body text-sm font-normal text-text-primary shadow-none hover:bg-[#f6f4ef]"
            iconClassName="size-4"
          />
        </div>

        <div className="mt-2 overflow-hidden rounded-2xl border border-[#dfddd8] bg-white">
          {isPending ? (
            <p className="px-4 py-6 font-body text-sm text-text-secondary">
              Loading…
            </p>
          ) : noActiveWithPast ? (
            <NoActiveMentorship isMentor={isMentor} />
          ) : !hasCurrentRows ? (
            <p className="px-4 py-6 font-body text-sm text-text-secondary">
              No mentorships yet.
            </p>
          ) : (
            <>
              <div className={`${ROW_GRID} border-b border-[#cbc6be]`}>
                <div className="flex h-14 items-center border-r border-[#cbc6be] px-4">
                  <SortHeader
                    label="Name"
                    column="name"
                    sort={activeSort}
                    onSort={toggleActiveSort}
                  />
                </div>
                <div className="flex h-14 items-center px-4">
                  <SortHeader
                    label="Days in mentorship"
                    column="days"
                    sort={activeSort}
                    onSort={toggleActiveSort}
                  />
                </div>
              </div>
              {activeRows.map((mentorship) => (
                <MentorshipRow
                  key={mentorship.mentorship_id}
                  mentorship={mentorship}
                  isMentor={isMentor}
                  userId={user.id}
                  hasActiveMentorship={grouped.active.length > 0}
                />
              ))}
              {grouped.paused.length > 0 ? (
                <>
                  <GroupToggle
                    label={`Paused · ${grouped.paused.length}`}
                    open={pausedOpen}
                    onToggle={() => setPausedOpen((open) => !open)}
                  />
                  {pausedOpen
                    ? grouped.paused.map((mentorship) => (
                        <MentorshipRow
                          key={mentorship.mentorship_id}
                          mentorship={mentorship}
                          isMentor={isMentor}
                          userId={user.id}
                          hasActiveMentorship={grouped.active.length > 0}
                          note="Paused"
                        />
                      ))
                    : null}
                </>
              ) : null}
            </>
          )}
        </div>

        {!isPending && grouped.past.length > 0 ? (
          <div className="mt-4 overflow-hidden rounded-3xl border border-[#dfddd8] bg-white">
            <GroupToggle
              label={`${pastLabel} (${grouped.past.length})`}
              open={pastOpen}
              onToggle={() => setPastOpen((open) => !open)}
            />
            {pastOpen
              ? grouped.past.map((mentorship) => (
                  <MentorshipRow
                    key={mentorship.mentorship_id}
                    mentorship={mentorship}
                    isMentor={isMentor}
                    userId={user.id}
                    hasActiveMentorship={grouped.active.length > 0}
                    showNote
                  />
                ))
              : null}
          </div>
        ) : null}
      </section>
    </div>
  );
}

type TeamAssignmentRole = "supervisor" | "lead_mentor";

const TEAM_ASSIGNMENT_COPY: Record<
  TeamAssignmentRole,
  {
    title: string;
    description: string;
    placeholder: string;
    fallbackName: string;
  }
> = {
  supervisor: {
    title: "Supervisor",
    description: "Choose a supervisor.",
    placeholder: "Select a supervisor",
    fallbackName: "Supervisor",
  },
  lead_mentor: {
    title: "Lead mentor",
    description: "Choose a lead mentor.",
    placeholder: "Select a lead mentor",
    fallbackName: "Lead mentor",
  },
};

function TeamAssignmentEditor({
  mentorId,
  currentId,
  role,
}: {
  mentorId: string;
  currentId: string | null;
  role: TeamAssignmentRole;
}) {
  const ability = useAbility();
  const { toast } = useToast();
  const canAssign = ability.can("assign", "supervisor");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(currentId ?? "");
  const assign = useAssignSupervisorMentor();
  const people = useUsers({
    page: 1,
    perPage: 10000,
    assignedRole: role,
    status: "active",
    enabled: open && canAssign,
  });
  const copy = TEAM_ASSIGNMENT_COPY[role];

  if (!canAssign) return null;

  const choices = [...(people.data?.users ?? [])]
    .map((person) => ({
      id: person.id,
      label: person.name ?? copy.fallbackName,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <>
      <button
        type="button"
        className="h-8 shrink-0 rounded-full border border-[#dfddd8] bg-white px-3 font-body text-sm text-text-primary hover:bg-[#f6f4ef]"
        onClick={() => {
          setSelected(currentId ?? "");
          setOpen(true);
        }}
      >
        Edit
      </button>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (assign.isPending) return;
          setOpen(next);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{copy.title}</DialogTitle>
            <DialogDescription>{copy.description}</DialogDescription>
          </DialogHeader>
          {people.isPending ? (
            <div className="flex h-10 items-center gap-2 font-body text-sm text-text-secondary">
              <Loader2 className="size-4 animate-spin" />
              Loading
            </div>
          ) : (
            <Select
              value={selected || undefined}
              onValueChange={setSelected}
              disabled={assign.isPending}
            >
              <SelectTrigger>
                <SelectValue placeholder={copy.placeholder} />
              </SelectTrigger>
              <SelectContent>
                {choices.map((choice) => (
                  <SelectItem key={choice.id} value={choice.id}>
                    {choice.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <button
            type="button"
            className="mt-4 inline-flex h-10 items-center justify-center gap-2 rounded-full bg-text-primary px-4 font-body text-sm font-medium text-white disabled:opacity-50"
            disabled={
              !selected ||
              selected === currentId ||
              assign.isPending ||
              people.isPending
            }
            onClick={() => {
              assign.mutate(
                { mentorId, supervisorId: selected },
                {
                  onSuccess: () => setOpen(false),
                  onError: () => {
                    toast({
                      title: "Couldn't update the assignment",
                      variant: "destructive",
                    });
                  },
                },
              );
            }}
          >
            {assign.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : null}
            {assign.isPending ? "Saving" : "Save"}
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
}

function TeamSkeleton() {
  return (
    <section aria-busy="true" aria-label="Loading team">
      <h2 className="font-heading text-base font-bold leading-6 text-text-primary">
        Team
      </h2>
      <div className="mt-2 flex flex-wrap gap-4">
        <div className="flex min-h-14 min-w-60 flex-1 items-center gap-4 rounded-3xl border border-[#dfddd8] bg-white px-4 py-3">
          <Skeleton className="size-8 shrink-0 rounded-full" />
          <div className="min-w-0">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="mt-1 h-3 w-20" />
          </div>
        </div>
      </div>
    </section>
  );
}

function TeamSlot({
  emptyLabel,
  people,
  action,
}: {
  emptyLabel: string;
  people: {
    id: string;
    name: string | null;
    profilePictureUrl?: string | null;
    roleLabel: string;
  }[];
  action: ReactNode;
}) {
  return (
    <div className="flex min-h-14 min-w-60 flex-1 items-center gap-4 rounded-3xl border border-[#dfddd8] bg-white px-4 py-3">
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        {people.length === 0 ? (
          <p className="font-body text-base leading-6 text-text-secondary">
            {emptyLabel}
          </p>
        ) : (
          people.map((person) => (
            <TeamPerson key={person.id} person={person} />
          ))
        )}
      </div>
      {action}
    </div>
  );
}

function TeamPerson({
  person,
}: {
  person: {
    id: string;
    name: string | null;
    profilePictureUrl?: string | null;
    roleLabel: string;
  };
}) {
  const photo = useSignedMedia(person.profilePictureUrl, person.id);
  const name = person.name ?? "";

  return (
    <div className="flex min-w-0 items-center gap-4">
      <UserAvatar name={name} imageUrl={photo.url} size={32} />
      <div className="min-w-0">
        <Link
          to="/users/$userId"
          params={{ userId: person.id }}
          className="block truncate font-body text-base leading-6 text-text-primary hover:underline focus-visible:underline"
        >
          {name}
        </Link>
        <p className="font-body text-xs leading-4 text-text-tertiary">
          {person.roleLabel}
        </p>
      </div>
    </div>
  );
}

function NoActiveMentorship({ isMentor }: { isMentor: boolean }) {
  return (
    <div className="flex flex-col items-center px-6 py-10 text-center">
      <p className="font-heading text-base font-bold leading-6 text-text-primary">
        No active mentorship
      </p>
      <p className="mt-1 max-w-md font-body text-sm leading-5 text-text-secondary">
        {isMentor
          ? "This mentor has no active mentee. Reactivate a past mentee below, or add a new one."
          : "This teen has no active mentor. Reactivate a past mentor below, or add a new one."}
      </p>
    </div>
  );
}

function SortHeader({
  label,
  column,
  sort,
  onSort,
}: {
  label: string;
  column: MentorshipSortColumn;
  sort: ActiveMentorshipSort | null;
  onSort: (column: MentorshipSortColumn) => void;
}) {
  const active = sort?.column === column;
  const direction = active ? sort.direction : "none";

  return (
    <button
      type="button"
      className="inline-flex items-center gap-2 text-left font-body text-base text-text-secondary"
      aria-sort={
        direction === "asc"
          ? "ascending"
          : direction === "desc"
            ? "descending"
            : "none"
      }
      onClick={() => onSort(column)}
    >
      {label}
      {direction === "asc" ? (
        <ChevronUp className="size-4" aria-hidden />
      ) : direction === "desc" ? (
        <ChevronDown className="size-4" aria-hidden />
      ) : (
        <ChevronsUpDown className="size-4" aria-hidden />
      )}
    </button>
  );
}

function GroupToggle({
  label,
  open,
  onToggle,
}: {
  label: string;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      className="flex h-11 w-full items-center gap-2 border-t border-[#e5e0d2] bg-[#f6f4ef] px-5 font-body text-sm leading-5 text-text-secondary"
      aria-expanded={open}
      onClick={onToggle}
    >
      {open ? (
        <ChevronDown className="size-4" aria-hidden />
      ) : (
        <ChevronRight className="size-4" aria-hidden />
      )}
      {label}
    </button>
  );
}

function MentorshipRowMenu({
  mentorship,
  userId,
  reactivateDisabled,
}: {
  mentorship: Mentorship;
  userId: string;
  reactivateDisabled: boolean;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const showDeactivate = !mentorship.deactivated;
  const showReactivate = Boolean(mentorship.deactivated);

  if (!showDeactivate && !showReactivate) return null;

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Mentorship actions"
            className="inline-flex size-10 items-center justify-center rounded-full text-text-primary hover:bg-[#f6f4ef]"
          >
            <Ellipsis className="size-6" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {showDeactivate ? (
            <DropdownMenuItem onSelect={() => setConfirmOpen(true)}>
              Deactivate
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              disabled={reactivateDisabled}
              onSelect={() => {
                if (reactivateDisabled) return;
                setConfirmOpen(true);
              }}
            >
              Reactivate
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {confirmOpen ? (
        <UpdateMentorship
          mentorshipId={mentorship.mentorship_id}
          userId={userId}
          deactivated={!showDeactivate}
          disabled={showReactivate && reactivateDisabled}
          trigger={null}
          open
          onOpenChange={setConfirmOpen}
        />
      ) : null}
    </>
  );
}

function MentorshipRow({
  mentorship,
  isMentor,
  userId,
  hasActiveMentorship,
  showNote = false,
  note,
}: {
  mentorship: Mentorship;
  isMentor: boolean;
  userId: string;
  hasActiveMentorship: boolean;
  showNote?: boolean;
  note?: string;
}) {
  const name = isMentor ? mentorship.mentee_name : mentorship.mentor_name;
  const photo = useSignedMedia(
    isMentor
      ? mentorship.mentee_profile_picture_url
      : mentorship.mentor_profile_picture_url,
  );
  const counterpartyDeactivated = isMentor
    ? mentorship.mentee_deactivated
    : mentorship.mentor_deactivated;
  const rematchName = mentorship.rematched_to_name;
  const rematchId = mentorship.rematched_to_id;
  const showRematch = Boolean(showNote && isMentor && rematchId && rematchName);
  const reactivateDisabled = reactivateMentorshipDisabled({
    isMentor,
    hasActiveMentorship,
    menteeDeactivated: Boolean(mentorship.mentee_deactivated),
    mentorDeactivated: Boolean(mentorship.mentor_deactivated),
  });

  return (
    <div
      className={`${ROW_GRID} relative border-b border-[#dfddd8] last:border-b-0`}
    >
      <div className="flex min-w-0 items-center gap-4 px-4 py-3">
        <div className="relative shrink-0">
          <UserAvatar name={name} imageUrl={photo.url} size={32} />
          {showNote && (showRematch || counterpartyDeactivated) ? (
            <span
              className="absolute -bottom-0.5 -right-0.5 size-3 rounded-full border-2 border-white bg-states-success"
              aria-hidden
            />
          ) : null}
        </div>
        <div className="min-w-0">
          {isMentor ? (
            <Link
              to="/users/$userId"
              params={{ userId: mentorship.mentee_id }}
              className="block truncate font-body text-base font-medium leading-6 text-text-primary"
            >
              {name}
            </Link>
          ) : (
            <p className="truncate font-body text-base font-medium leading-6 text-text-primary">
              {name}
            </p>
          )}
          {showRematch && rematchId && rematchName ? (
            <p className="truncate font-body text-xs leading-4 text-text-tertiary">
              rematched to{" "}
              <Link
                to="/users/$userId"
                params={{ userId: rematchId }}
                className="text-states-hyperlinked underline decoration-states-hyperlinked underline-offset-2"
              >
                {rematchName}
              </Link>
            </p>
          ) : null}
          {showNote && !showRematch && counterpartyDeactivated ? (
            <p className="font-body text-xs leading-4 text-text-tertiary">
              deactivated
            </p>
          ) : null}
          {note && !showRematch ? (
            <p className="font-body text-xs leading-4 text-text-tertiary">
              {note}
            </p>
          ) : null}
        </div>
      </div>
      <p className="px-4 py-3 pr-52 font-body text-base leading-6 text-text-primary">
        {formatDaysInMentorship(mentorship.days_in_mentorship)}
      </p>
      <div className="absolute right-4 top-1/2 flex -translate-y-1/2 items-center gap-6">
        <Link
          to="/mentorships/$mentorshipId"
          params={{ mentorshipId: mentorship.mentorship_id }}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-full px-3 font-body text-sm font-bold text-text-primary hover:bg-[#f6f4ef]"
        >
          <MessageCircle className="size-6" />
          View chat
        </Link>
        <MentorshipRowMenu
          mentorship={mentorship}
          userId={userId}
          reactivateDisabled={reactivateDisabled}
        />
      </div>
    </div>
  );
}
